import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { withRouter, Switch as RouterSwitch } from 'react-router-dom'
import { Button, Modal, Table, Tag, InputNumber, Space, Empty } from 'antd'
import PageLoader from '../../../components/Layouts/PageLoader'
import PrivateRoute from '../../../hocs/PrivateRoute'
import PaginationContainer from '../../../components/Forms/Pagination'
import {
  getRequest,
  patchRequest,
  formatUrl,
  dutyPricingUrl,
  successNotification,
  errorNotification
} from '../../../config'
import { isUnsetPrice, formatPrice, isValidPrice } from '../utils'

const PricingList = () => {
  const [loading, setLoading] = useState(false)
  const [bodyTypes, setBodyTypes] = useState([])
  const [meta, setMeta] = useState({ previous: null, next: null, count: 0 })
  const [editing, setEditing] = useState(null)
  const [priceValue, setPriceValue] = useState(null)
  const [discountValue, setDiscountValue] = useState(null)
  const [premiumValue, setPremiumValue] = useState(null)
  const [saving, setSaving] = useState(false)

  // `url` is a paginator next/previous link when paging; otherwise load page 1.
  const getPrices = async (url = null) => {
    const response = await getRequest(
      url ? formatUrl(url) : dutyPricingUrl,
      'body-type prices',
      setLoading
    )
    if (response.success) {
      setBodyTypes(response.data.results || response.data)
      setMeta({ ...response.data })
    }
  }

  useEffect(() => {
    getPrices()
    // eslint-disable-next-line
  }, [])

  const openEdit = record => {
    setEditing(record)
    setPriceValue(record.servicePrice ? Number(record.servicePrice) : 0)
    setDiscountValue(
      record.ageDiscountAmount ? Number(record.ageDiscountAmount) : 0
    )
    // Absent against an API that predates the field — treated as 0 so the page works either way.
    setPremiumValue(
      record.vintagePremiumAmount ? Number(record.vintagePremiumAmount) : 0
    )
  }

  const handleSave = async () => {
    if (
      !isValidPrice(priceValue) ||
      !isValidPrice(discountValue) ||
      !isValidPrice(premiumValue)
    ) {
      errorNotification('Enter valid non-negative amounts')
      return
    }
    setSaving(true)
    const response = await patchRequest(
      `${dutyPricingUrl}/${editing.id}/`,
      {
        servicePrice: priceValue,
        ageDiscountAmount: discountValue,
        vintagePremiumAmount: premiumValue
      },
      'price',
      () => {}
    )
    setSaving(false)
    if (response.success) {
      successNotification('Pricing updated')
      setEditing(null)
      getPrices()
    } else {
      errorNotification('Could not update the pricing, retry.')
    }
  }

  const isUnset = isUnsetPrice

  const columns = [
    {
      title: 'Body Type',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span>
          <b>{name}</b>
          {isUnset(record) && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              No price set
            </Tag>
          )}
        </span>
      )
    },
    {
      title: 'Service Price',
      dataIndex: 'servicePrice',
      key: 'servicePrice',
      render: (price, record) => formatPrice(record)
    },
    {
      title: 'Old-car discount',
      dataIndex: 'ageDiscountAmount',
      key: 'ageDiscountAmount',
      render: amount =>
        Number(amount || 0) > 0 ? (
          <span style={{ color: '#389e0d' }}>
            &minus; GHS {Number(amount).toFixed(2)}
          </span>
        ) : (
          <span style={{ color: '#bbb' }}>&mdash;</span>
        )
    },
    {
      // Signed and coloured, because two adjacent money columns are otherwise skimmed as the same
      // kind of thing — and one of them is a charge.
      title: 'Vintage premium',
      dataIndex: 'vintagePremiumAmount',
      key: 'vintagePremiumAmount',
      render: amount =>
        Number(amount || 0) > 0 ? (
          <span style={{ color: '#d46b08' }}>
            + GHS {Number(amount).toFixed(2)}
          </span>
        ) : (
          <span style={{ color: '#bbb' }}>&mdash;</span>
        )
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Button size="small" onClick={() => openEdit(record)}>
          Edit Pricing
        </Button>
      )
    }
  ]

  return (
    <PageLoader className="container" loading={loading}>
      <div className="card mb-5rem">
        <div className="card-body">
          <h3 style={{ marginBottom: 16 }}>
            Body Type Prices {meta && `(${meta.count})`}
          </h3>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Button onClick={() => getPrices()}>Refresh</Button>
            </Space>
            <p style={{ marginTop: 8, color: '#888' }}>
              Flat duty-service fee charged per car of each body type (GHS). A body type with
              no price set blocks the paywall for that car.
            </p>
            <p style={{ marginTop: 4, color: '#888' }}>
              Age changes the fee in one of two ways, never both. Cars <b>9 years and older</b>{' '}
              get the old-car discount taken off. Cars <b>20 years and older</b> are vintage:
              they pay the premium added on instead, and are <b>not</b> discounted.
            </p>
          </div>

          <Table
            rowKey="id"
            dataSource={bodyTypes}
            columns={columns}
            pagination={false}
            locale={{ emptyText: <Empty description="No body types" /> }}
          />
          {bodyTypes.length ? (
            <PaginationContainer meta={meta} getData={url => getPrices(url)} />
          ) : null}
        </div>
      </div>

      <Modal
        title={`Set pricing — ${editing?.name || ''}`}
        centered
        open={!!editing}
        onOk={handleSave}
        onCancel={() => setEditing(null)}
        okText="Save"
        confirmLoading={saving}
      >
        <label>Service price (GHS)</label>
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={priceValue}
          onChange={setPriceValue}
        />
        <label style={{ display: 'block', marginTop: 16 }}>
          Old-car discount (GHS)
        </label>
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={discountValue}
          onChange={setDiscountValue}
        />
        <p style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
          Taken off the fee for cars 9 years and older, up to 20. Set 0 for no discount.
        </p>
        <label style={{ display: 'block', marginTop: 16 }}>
          Vintage premium (GHS)
        </label>
        <InputNumber
          min={0}
          precision={2}
          style={{ width: '100%' }}
          value={premiumValue}
          onChange={setPremiumValue}
        />
        <p style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
          <b>Added to</b> the fee for cars 20 years and older, instead of the discount. Set 0 for
          no premium on this body type.
        </p>
      </Modal>
    </PageLoader>
  )
}

const PricingRoutes = props => (
  <RouterSwitch>
    <PrivateRoute exact path={props.match.path} component={PricingList} />
  </RouterSwitch>
)

PricingRoutes.propTypes = {
  match: PropTypes.object
}

export default withRouter(PricingRoutes)
